/** Reine, ohne OpenCV/DOM testbare Geometrie-Funktionen für den Foto-Zuschnitt
 *  (Inkrement 3: Perspektivkorrektur der Kamera-Unterschriftsfotos). */

export interface Point {
  x: number;
  y: number;
}

/** Ordnet 4 beliebig sortierte Eckpunkte konsistent als
 *  [oben-links, oben-rechts, unten-rechts, unten-links] — Standard-Heuristik:
 *  oben-links = kleinste Summe (x+y), unten-rechts = größte Summe;
 *  oben-rechts = kleinste Differenz (y-x), unten-links = größte Differenz. */
export function orderCorners(pts: Point[]): [Point, Point, Point, Point] {
  const bySum = [...pts].sort((a, b) => a.x + a.y - (b.x + b.y));
  const byDiff = [...pts].sort((a, b) => a.y - a.x - (b.y - b.x));
  const topLeft = bySum[0];
  const bottomRight = bySum[3];
  const topRight = byDiff[0];
  const bottomLeft = byDiff[3];
  return [topLeft, topRight, bottomRight, bottomLeft];
}

/** Start-Rechteck mit Rand vom Bildrand, falls die Kanten-Erkennung nichts Eindeutiges
 *  findet — der Kunde zieht die Ecken dann selbst zurecht (kein Fehler, kein Blocker). */
export function defaultCorners(width: number, height: number, marginRatio = 0.05): [Point, Point, Point, Point] {
  const mx = width * marginRatio;
  const my = height * marginRatio;
  return [
    { x: mx, y: my },
    { x: width - mx, y: my },
    { x: width - mx, y: height - my },
    { x: mx, y: height - my },
  ];
}

/** Ziel-Rechteckgröße aus den (ggf. leicht schrägen) 4 Ecken — jeweils die längere
 *  gegenüberliegende Seite, damit beim Entzerren nichts vom Original verloren geht. */
export function outputSizeFromCorners(
  [topLeft, topRight, bottomRight, bottomLeft]: [Point, Point, Point, Point],
): { width: number; height: number } {
  const dist = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);
  const width = Math.round(Math.max(dist(topLeft, topRight), dist(bottomLeft, bottomRight)));
  const height = Math.round(Math.max(dist(topLeft, bottomLeft), dist(topRight, bottomRight)));
  return { width: Math.max(width, 1), height: Math.max(height, 1) };
}

/** Begrenzt eine Zielgröße proportional auf eine maximale längere Seite — hält die
 *  Pixel-Schleife der reinen JS-Perspektivkorrektur (siehe document-scan.ts) auch bei
 *  hochauflösenden Kamerafotos (12+ MP) schnell; ein signiertes Dokument braucht keine
 *  Foto-Auflösung, um lesbar zu bleiben. */
export function capOutputSize(
  size: { width: number; height: number },
  maxDim: number,
): { width: number; height: number } {
  const longest = Math.max(size.width, size.height);
  if (longest <= maxDim) return size;
  const scale = maxDim / longest;
  return { width: Math.max(1, Math.round(size.width * scale)), height: Math.max(1, Math.round(size.height * scale)) };
}

/** Löst A·x = b per Gauß-Elimination mit Spaltenpivotisierung (kleine, feste Größe hier
 *  immer 8×8 — für computeHomography). */
function solveLinearSystem(a: number[][], b: number[]): number[] {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r;
    }
    [m[col], m[pivot]] = [m[pivot], m[col]];
    const pivotVal = m[col][col];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = m[r][col] / pivotVal;
      for (let c = col; c <= n; c++) m[r][c] -= factor * m[col][c];
    }
  }
  return m.map((row, i) => row[n] / row[i]);
}

/** Row-major 3×3-Homographie (9 Werte, h[8]=1), die jeden `from[i]` exakt auf `to[i]`
 *  abbildet (Direct Linear Transform für genau 4 Punktepaare) — die reine Mathe hinter
 *  der Perspektivkorrektur, ohne OpenCV/WASM. */
export function computeHomography(
  from: [Point, Point, Point, Point],
  to: [Point, Point, Point, Point],
): number[] {
  const a: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = from[i];
    const { x: dx, y: dy } = to[i];
    a.push([x, y, 1, 0, 0, 0, -x * dx, -y * dx]);
    b.push(dx);
    a.push([0, 0, 0, x, y, 1, -x * dy, -y * dy]);
    b.push(dy);
  }
  return [...solveLinearSystem(a, b), 1];
}

/** Wendet eine row-major 3×3-Homographie (9 Werte) inkl. perspektivischer Division auf
 *  einen Punkt an. */
export function applyHomography(h: number[], p: Point): Point {
  const w = h[6] * p.x + h[7] * p.y + h[8];
  return {
    x: (h[0] * p.x + h[1] * p.y + h[2]) / w,
    y: (h[3] * p.x + h[4] * p.y + h[5]) / w,
  };
}
